import { pxToEm } from 'util/pxToEm';
import React, { ReactElement, useState, useEffect, ChangeEvent } from 'react';
import { Box, Typography, CircularProgress, Checkbox, Link } from '@mui/material';
import { Image } from 'components/lib';
import { colorLightGrey } from 'customColors';
import { ORGANIZATION_ROLES } from 'enum/organization-roles';
import { OrderApproveIcon } from 'components/icons';
import { useOrganizationLeaderTerms } from 'utils/organization-leader-terms';
import { Select } from '../Select';
import { Modal } from '../Modal';

export interface RoleSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (role: string) => Promise<void>;
  organizationName: string;
  organizationLogoUrl?: string;
  initialRole?: string;
  isEditMode?: boolean;
  schoolTermsUrl?: string;
  schoolTermsDisplayName?: string;
}

export const RoleSelectionModal = ({
  open,
  onClose,
  onSubmit,
  organizationName,
  organizationLogoUrl,
  initialRole = '',
  isEditMode = false,
  schoolTermsUrl,
  schoolTermsDisplayName,
}: RoleSelectionModalProps): ReactElement => {
  const [selectedRole, setSelectedRole] = useState<string>(initialRole);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const { termsUrl, termsDisplayText, checkboxText } = useOrganizationLeaderTerms({
    termsUrl: schoolTermsUrl,
    termsDisplayName: schoolTermsDisplayName,
  });

  // Reset role and terms when modal opens/closes or initialRole changes
  useEffect(() => {
    if (open) {
      setSelectedRole(initialRole);
      setAcceptedTerms(false);
    }
  }, [open, initialRole]);

  const handleRoleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedRole(event.target.value);
  };

  const handleSubmit = async () => {
    if (!selectedRole || isSubmitting || !acceptedTerms) return;

    setIsSubmitting(true);
    try {
      await onSubmit(selectedRole);
      onClose();
    } catch (error) {
      // Error is handled by parent component
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled = !selectedRole || isSubmitting || !acceptedTerms;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Get added as a leader"
      headerStyle={{
        textAlign: 'left',
        justifyContent: 'start',
        fontWeight: 600,
        color: '#383838',
      }}
      contentStyle={{
        width: { xs: '100%', sm: '100%', md: 600 },
        minWidth: { xs: '100%', sm: '100%', md: 600 },
        maxWidth: { xs: '100%', sm: '100%', md: 600 },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
        {/* Organization Logo and Name */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 12,
            width: '100%',
            mb: 32,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              flexShrink: 0,
              borderRadius: 4,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: colorLightGrey,
            }}
          >
            {organizationLogoUrl ? (
              <Image
                src={organizationLogoUrl}
                alt={organizationName}
                width="100%"
                height="100%"
                style={{
                  objectFit: 'contain',
                }}
              />
            ) : (
          <Typography
            sx={{
              fontSize: pxToEm(20),
              fontWeight: 600,
            }}
          >
            {organizationName.charAt(0).toUpperCase()}
          </Typography>
            )}
          </Box>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: pxToEm(18),
              lineHeight: '27px',
              color: '#383838',
            }}
          >
            {organizationName}
          </Typography>
        </Box>

        {/* Role Dropdown */}
        <Box sx={{ mb: 16, width: '100%', maxWidth: 320 }}>
          <Select
            label="Role"
            placeholder="Select a role"
            value={selectedRole}
            onChange={handleRoleChange}
          >
            {ORGANIZATION_ROLES.map(role => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </Select>
        </Box>

        {/* Helper Text */}
        <Typography
          sx={{
            fontSize: pxToEm(12),
            fontWeight: 500,
            textAlign: 'center',
            lineHeight: '18px',
            mb: 16,
          }}
        >
          Your advisor/student affairs leader will verify your role.
        </Typography>

        {/* Terms Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            mb: 16,
            p: 12,
            border: '1px solid #E0E0E0',
            borderRadius: '4px',
            backgroundColor: '#FAFAFA',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <OrderApproveIcon fontSize="small" sx={{ color: '#383838' }} />
            <Link
              href={termsUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                fontSize: pxToEm(12),
                fontWeight: 500,
                color: '#23305F',
                textDecoration: 'underline',
                '&:hover': {
                  opacity: 0.7,
                },
              }}
            >
              {termsDisplayText}
            </Link>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Checkbox
              checked={acceptedTerms}
              onChange={e => setAcceptedTerms(e.target.checked)}
              disabled={isSubmitting}
              sx={{
                p: 0,
                '&.Mui-checked': {
                  color: '#23305F',
                },
              }}
            />
            <Typography
              sx={{
                fontSize: pxToEm(12),
                fontWeight: 400,
                color: '#383838',
                lineHeight: '18px',
              }}
            >
              {checkboxText}
            </Typography>
          </Box>
        </Box>

        {/* Submit Button */}
        <Box
          component="button"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          sx={{
            width: 320,
            maxWidth: 320,
            height: 47,
            alignSelf: 'stretch',
            backgroundColor: isSubmitDisabled ? '#F0F0F0' : '#FFE733',
            color: isSubmitDisabled ? '#9E9E9E' : '#23305F',
            opacity: isSubmitDisabled ? 0.6 : 1,
            fontWeight: 400,
            fontSize: pxToEm(14),
            borderRadius: '6px',
            border: 'none',
            cursor: isSubmitDisabled ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 24,
            '&:hover': {
              backgroundColor: isSubmitDisabled ? '#F0F0F0' : '#FFD900',
            },
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={20} sx={{ color: '#23305F' }} />
          ) : isEditMode ? (
            'Update request'
          ) : (
            'Submit for verification'
          )}
        </Box>

        {/* Back Link */}
        <Box className="flex justify-center" sx={{ width: '100%', mt: 20 }}>
          <Typography
            onClick={onClose}
            sx={{
              fontSize: pxToEm(14),
              fontWeight: 400,
              color: '#23305F',
              textAlign: 'center',
              cursor: 'pointer',
              pb: 16,
              '&:hover': {
                opacity: 0.7,
              },
            }}
          >
            Back
          </Typography>
        </Box>
      </Box>
    </Modal>
  );
};
